import { CONSUMPTION_TIERS, PULSE_BASE_SPEED } from '../config/balance.js';
import { COLOR_HEX, UI_PALETTE, type ColorId } from '../config/colors.js';
import {
  bulbInflowPerSecond,
  bulbMaxEnergy,
  consumptionRateForTier,
  effectiveGeneratorProductionPerSecond,
  networkEnergyStatsForGenerator,
} from '../engine/energy.js';
import { cellKey, cellCenterCoord, nodeOccupiesCell, nodePortCell } from '../engine/grid.js';
import {
  buildNetworkAnalysis,
  edgeEffectiveThroughput,
  edgeKeyFromCells,
  hasColorInterferenceAt,
  listInterferenceCells,
} from '../engine/network.js';
import type { Simulation } from '../engine/simulation.js';
import type { CellCoord, GameNode } from '../engine/types.js';
import type { GameSettings } from '../app/settings.js';
import { Camera } from './camera.js';
import { ParticlePool } from './particles.js';

export interface RenderOverlay {
  previewPath: CellCoord[] | null;
  previewValid: boolean;
  /** Live rubber-band from tip cell center while drawing (world coords). */
  draftStretch: {
    fromX: number;
    fromY: number;
    toX: number;
    toY: number;
    progress: number;
    valid: boolean;
  } | null;
  hoverCell: CellCoord | null;
  tutorialHighlight: boolean;
  gameOverFade: number;
  spawnAnim: Map<string, number>;
}

export class GameRenderer {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  camera = new Camera();
  particles = new ParticlePool();
  overlay: RenderOverlay = {
    previewPath: null,
    previewValid: true,
    draftStretch: null,
    hoverCell: null,
    tutorialHighlight: false,
    gameOverFade: 0,
    spawnAnim: new Map(),
  };
  private edgeLoadVisual = new Map<string, number>();
  private pulseTrails = new Map<string, { x: number; y: number; t: number }[]>();
  /** Display-smoothed world positions — hides fixed-step / congestion stutter. */
  private pulseSmoothPos = new Map<string, { x: number; y: number }>();
  private time = 0;
  private settings: GameSettings;
  private bgGradient: CanvasGradient | null = null;
  private bgSize = { w: 0, h: 0 };

  constructor(canvas: HTMLCanvasElement, settings: GameSettings) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('no canvas');
    this.ctx = ctx;
    this.settings = settings;
  }

  updateSettings(s: GameSettings): void {
    this.settings = s;
  }

  resize(): void {
    const parent = this.canvas.parentElement;
    if (!parent) return;
    const dpr = Math.min(window.devicePixelRatio || 1, this.settings.quality === 'low' ? 1 : 2);
    const w = parent.clientWidth;
    const h = parent.clientHeight;
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  noteSpawn(nodeId: string): void {
    this.overlay.spawnAnim.set(nodeId, 0);
  }

  pulseWorldPos(
    pulse: { cableId: string; segmentIndex: number; segmentT: number; forward: boolean },
    cableById: Map<string, { cells: CellCoord[] }>,
    extrudeSeconds = 0,
    speedMult = 1,
  ): { x: number; y: number; dx: number; dy: number } | null {
    const cable = cableById.get(pulse.cableId);
    if (!cable) return null;
    const a = cable.cells[pulse.segmentIndex];
    const b = cable.cells[pulse.segmentIndex + 1];
    if (!a || !b) return null;
    const forward = pulse.forward !== false;
    const from = forward ? a : b;
    const to = forward ? b : a;
    // Stay on the current segment — avoids visual overshoot while waiting on congestion.
    const t = Math.min(
      1 - 1e-6,
      Math.max(0, pulse.segmentT + PULSE_BASE_SPEED * speedMult * extrudeSeconds),
    );
    return {
      x: from.x + 0.5 + (to.x - from.x) * t,
      y: from.y + 0.5 + (to.y - from.y) * t,
      // Unit travel direction (orthogonal cables → ±1 on one axis).
      dx: Math.sign(to.x - from.x),
      dy: Math.sign(to.y - from.y),
    };
  }

  /**
   * When several pulses sit on the same spot, spread them along the cable
   * segment (travel direction) so queues are readable (visual only).
   */
  private buildPulseStackOffsets(
    pulses: { id: string; cableId: string; segmentIndex: number; segmentT: number; forward: boolean }[],
    cableById: Map<string, { cells: CellCoord[] }>,
    extrudeSeconds: number,
    speedMult: number,
  ): Map<string, { ox: number; oy: number }> {
    const offsets = new Map<string, { ox: number; oy: number }>();
    type Entry = {
      id: string;
      t: number;
      dx: number;
      dy: number;
      cableId: string;
      segmentIndex: number;
    };
    // Bucket by cable segment — a queue waiting at a cell boundary shares one.
    const buckets = new Map<string, Entry[]>();

    for (const pulse of pulses) {
      const pos = this.pulseWorldPos(pulse, cableById, extrudeSeconds, speedMult);
      if (!pos) continue;
      const t = Math.min(
        1 - 1e-6,
        Math.max(
          0,
          pulse.segmentT + PULSE_BASE_SPEED * speedMult * extrudeSeconds,
        ),
      );
      // Same segment + similar t → same visual pile (queued at a bottleneck).
      const tBucket = Math.round(t * 8);
      const key = `${pulse.cableId}|${pulse.segmentIndex}|${tBucket}|${pulse.forward !== false ? 1 : 0}`;
      let list = buckets.get(key);
      if (!list) {
        list = [];
        buckets.set(key, list);
      }
      list.push({
        id: pulse.id,
        t,
        dx: pos.dx,
        dy: pos.dy,
        cableId: pulse.cableId,
        segmentIndex: pulse.segmentIndex,
      });
    }

    // Spread along the segment in world units (one cell ≈ length 1).
    const spacing = 0.22;
    const maxSpread = 0.7;
    for (const list of buckets.values()) {
      if (list.length < 2) continue;
      // Stable order: older / lower id first so the convoy looks consistent.
      list.sort((a, b) => a.id.localeCompare(b.id));
      const n = list.length;
      const step = Math.min(spacing, (2 * maxSpread) / Math.max(1, n - 1));
      const dx = list[0]!.dx;
      const dy = list[0]!.dy;
      // Prefer spreading backward along the cable (behind the lead pulse).
      const backX = dx === 0 && dy === 0 ? -1 : -dx;
      const backY = dx === 0 && dy === 0 ? 0 : -dy;
      for (let i = 0; i < n; i++) {
        const e = list[i]!;
        // i=0 stays near the front; later pulses trail behind on the tronçon.
        offsets.set(e.id, { ox: backX * i * step, oy: backY * i * step });
      }
    }
    return offsets;
  }

  private pulseVisualSpeedMult(sim: Simulation): number {
    const mods = sim.state.runModifiers;
    return (
      Math.max(0.05, mods.pulseSpeedMult || 1) * (mods.expressLine ? 1.15 : 1)
    );
  }

  pulsePosition(sim: Simulation, pulseId: string): { x: number; y: number } | null {
    const pulse = sim.state.pulses.find((p) => p.id === pulseId);
    if (!pulse) return null;
    const cableById = sim.getNetworkAnalysis().cableById;
    const extrude = sim.renderExtrudeSeconds();
    const speedMult = this.pulseVisualSpeedMult(sim);
    const pos = this.pulseWorldPos(pulse, cableById, extrude, speedMult);
    if (!pos) return null;
    const offsets = this.buildPulseStackOffsets(sim.state.pulses, cableById, extrude, speedMult);
    const o = offsets.get(pulseId);
    return o ? { x: pos.x + o.ox, y: pos.y + o.oy } : { x: pos.x, y: pos.y };
  }

  render(sim: Simulation, dt: number): void {
    this.time += dt;
    const state = sim.state;
    // Soft vignette only — never hide the fallen network.
    if (state.gameOver) {
      this.overlay.gameOverFade = Math.min(0.22, this.overlay.gameOverFade + dt * 0.35);
    }
    const pulseCount = state.pulses.length;
    const heavy = pulseCount > 60;
    const veryHeavy = pulseCount > 140;
    if (this.settings.particles && !veryHeavy) this.particles.update(dt);

    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    const ctx = this.ctx;
    if (!this.bgGradient || this.bgSize.w !== w || this.bgSize.h !== h) {
      this.bgGradient = ctx.createLinearGradient(0, 0, w, h);
      this.bgGradient.addColorStop(0, '#1a3a62');
      this.bgGradient.addColorStop(0.45, '#243f6b');
      this.bgGradient.addColorStop(1, '#1e3558');
      this.bgSize = { w, h };
    }
    ctx.fillStyle = this.bgGradient;
    ctx.fillRect(0, 0, w, h);

    this.drawBoardPlate(state.gridWidth, state.gridHeight);
    // Always draw the grid when enabled — never hide it under pulse load.
    if (this.settings.showGrid) {
      this.drawGrid(state.gridWidth, state.gridHeight, w, h);
    }
    this.drawHoverCell(state.gridWidth, state.gridHeight);
    this.drawTerrain(state);
    const analysis = sim.getNetworkAnalysis();
    this.drawCables(sim, analysis, heavy);
    if (!heavy) this.drawInterference(sim, analysis);
    if (this.overlay.draftStretch) this.drawDraftStretch(this.overlay.draftStretch);
    if (this.overlay.previewPath) this.drawPreview(this.overlay.previewPath, this.overlay.previewValid);
    if (this.overlay.tutorialHighlight) this.drawTutorialHint(state);
    this.drawNodes(sim, dt);
    const extrude = sim.renderExtrudeSeconds();
    const speedMult = this.pulseVisualSpeedMult(sim);
    const stackOffsets =
      pulseCount > 1
        ? this.buildPulseStackOffsets(state.pulses, analysis.cableById, extrude, speedMult)
        : null;
    const smoothPos = this.updatePulseSmoothPositions(
      state.pulses,
      analysis.cableById,
      extrude,
      speedMult,
      stackOffsets,
      dt,
    );
    if (!veryHeavy && this.settings.glow) {
      this.updateTrails(sim, dt, analysis.cableById, heavy, smoothPos);
    } else if (this.pulseTrails.size) this.pulseTrails.clear();
    this.drawPulses(sim, analysis.cableById, heavy, veryHeavy, smoothPos);
    if (this.settings.particles && !veryHeavy) this.particles.draw(ctx);
    this.drawNodeHoverTooltip(sim);

    if (this.overlay.gameOverFade > 0) {
      ctx.fillStyle = `rgba(11, 20, 37, ${this.overlay.gameOverFade * 0.35})`;
      ctx.fillRect(0, 0, w, h);
    }
  }

  /**
   * Exponential follow of sim targets so motion stays continuous even when
   * the fixed timestep or capacity queue stutters.
   */
  private updatePulseSmoothPositions(
    pulses: { id: string; cableId: string; segmentIndex: number; segmentT: number; forward: boolean }[],
    cableById: Map<string, { cells: CellCoord[] }>,
    extrudeSeconds: number,
    speedMult: number,
    stackOffsets: Map<string, { ox: number; oy: number }> | null,
    dt: number,
  ): Map<string, { x: number; y: number }> {
    const alpha = 1 - Math.exp(-22 * Math.max(0.001, dt));
    const alive = new Set<string>();
    const out = new Map<string, { x: number; y: number }>();
    for (const pulse of pulses) {
      alive.add(pulse.id);
      const raw = this.pulseWorldPos(pulse, cableById, extrudeSeconds, speedMult);
      if (!raw) continue;
      const o = stackOffsets?.get(pulse.id);
      const tx = raw.x + (o?.ox ?? 0);
      const ty = raw.y + (o?.oy ?? 0);
      const prev = this.pulseSmoothPos.get(pulse.id);
      const next = prev
        ? {
            x: prev.x + (tx - prev.x) * alpha,
            y: prev.y + (ty - prev.y) * alpha,
          }
        : { x: tx, y: ty };
      this.pulseSmoothPos.set(pulse.id, next);
      out.set(pulse.id, next);
    }
    for (const id of this.pulseSmoothPos.keys()) {
      if (!alive.has(id)) this.pulseSmoothPos.delete(id);
    }
    return out;
  }

  private updateTrails(
    sim: Simulation,
    dt: number,
    cableById: Map<string, { cells: CellCoord[] }>,
    heavy: boolean,
    smoothPos: Map<string, { x: number; y: number }>,
  ): void {
    const maxLen = heavy ? 4 : 10;
    const alive = new Set<string>();
    for (const p of sim.state.pulses) {
      alive.add(p.id);
      const pos = smoothPos.get(p.id);
      if (!pos) continue;
      const screen = this.camera.worldToScreen(pos.x, pos.y);
      let trail = this.pulseTrails.get(p.id);
      if (!trail) {
        trail = [];
        this.pulseTrails.set(p.id, trail);
      }
      trail.push({ x: screen.x, y: screen.y, t: heavy ? 0.18 : 0.35 });
      if (trail.length > maxLen) trail.shift();
      for (const pt of trail) pt.t -= dt;
      while (trail.length && trail[0]!.t <= 0) trail.shift();
    }
    for (const key of this.pulseTrails.keys()) {
      if (!alive.has(key)) this.pulseTrails.delete(key);
    }
  }

  private drawBoardPlate(gw: number, gh: number): void {
    const ctx = this.ctx;
    const tl = this.camera.worldToScreen(0, 0);
    const br = this.camera.worldToScreen(gw, gh);
    ctx.fillStyle = UI_PALETTE.board;
    ctx.fillRect(tl.x, tl.y, br.x - tl.x, br.y - tl.y);
    ctx.strokeStyle = 'rgba(61, 216, 255, 0.35)';
    ctx.lineWidth = 2;
    ctx.strokeRect(tl.x, tl.y, br.x - tl.x, br.y - tl.y);
  }

  private drawGrid(gw: number, gh: number, w: number, h: number): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.strokeStyle = 'rgba(140, 210, 255, 0.32)';
    ctx.lineWidth = 1;
    for (let y = 0; y <= gh; y++) {
      const a = this.camera.worldToScreen(0, y);
      const b = this.camera.worldToScreen(gw, y);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }
    for (let x = 0; x <= gw; x++) {
      const a = this.camera.worldToScreen(x, 0);
      const b = this.camera.worldToScreen(x, gh);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }
    if (this.settings.glow) {
      const c = this.camera.worldToScreen(gw / 2, gh / 2);
      const grad = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, Math.min(w, h) * 0.55);
      grad.addColorStop(0, 'rgba(255, 224, 102, 0.07)');
      grad.addColorStop(0.5, 'rgba(61, 216, 255, 0.05)');
      grad.addColorStop(1, 'transparent');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
    }
    ctx.restore();
  }

  /** Soft highlight on the hovered cell so the grid stays readable while drawing. */
  private drawHoverCell(gw: number, gh: number): void {
    const hover = this.overlay.hoverCell;
    if (!hover) return;
    if (hover.x < 0 || hover.y < 0 || hover.x >= gw || hover.y >= gh) return;
    const ctx = this.ctx;
    const p = this.camera.worldToScreen(hover.x, hover.y);
    const sz = this.camera.cellSize * this.camera.zoom;
    ctx.fillStyle = 'rgba(61, 216, 255, 0.12)';
    ctx.fillRect(p.x, p.y, sz, sz);
    ctx.strokeStyle = 'rgba(61, 216, 255, 0.45)';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(p.x + 0.5, p.y + 0.5, sz - 1, sz - 1);
  }

  private drawTerrain(state: Simulation['state']): void {
    const { terrain, gridWidth, gridHeight } = state;
    const ctx = this.ctx;
    for (let y = 0; y < gridHeight; y++) {
      for (let x = 0; x < gridWidth; x++) {
        const kind = terrain.cells[y * terrain.width + x];
        if (kind === 'empty') continue;
        const p = this.camera.worldToScreen(x, y);
        const sz = this.camera.cellSize * this.camera.zoom;
        if (kind === 'obstacle') {
          ctx.fillStyle = '#1a2d4a';
          ctx.fillRect(p.x + 2, p.y + 2, sz - 4, sz - 4);
          ctx.strokeStyle = 'rgba(255, 91, 110, 0.35)';
          ctx.lineWidth = 1;
          ctx.strokeRect(p.x + 4, p.y + 4, sz - 8, sz - 8);
          ctx.fillStyle = 'rgba(255, 91, 110, 0.15)';
          ctx.fillRect(p.x + sz * 0.3, p.y + sz * 0.35, sz * 0.15, sz * 0.1);
        } else if (kind === 'magnetic') {
          const shimmer = 0.35 + Math.sin(this.time * 3 + x + y) * 0.15;
          ctx.fillStyle = `rgba(180, 154, 255, ${shimmer * 0.35})`;
          ctx.fillRect(p.x + 1, p.y + 1, sz - 2, sz - 2);
          ctx.strokeStyle = `rgba(180, 154, 255, ${shimmer})`;
          ctx.strokeRect(p.x + 3, p.y + 3, sz - 6, sz - 6);
        }
      }
    }
  }

  private drawCables(
    sim: Simulation,
    analysis: ReturnType<typeof buildNetworkAnalysis>,
    heavy = false,
  ): void {
    const state = sim.state;
    const ctx = this.ctx;
    const feedByCable = analysis.feedColorByCableId;
    const useGlow = this.settings.glow && !heavy;

    for (const cable of state.cables) {
      const feed = feedByCable.get(cable.id) ?? null;
      for (let i = 0; i < cable.cells.length - 1; i++) {
        const c1 = cable.cells[i]!;
        const c2 = cable.cells[i + 1]!;
        const ek = edgeKeyFromCells(c1, c2);
        const throughput = edgeEffectiveThroughput(analysis, c1, c2, state.runModifiers);
        const util = Math.min(1, (this.edgeLoadVisual.get(ek) ?? 0) / Math.max(0.01, throughput));
        this.edgeLoadVisual.set(ek, (this.edgeLoadVisual.get(ek) ?? 0) * 0.92);

        const a = this.camera.worldToScreen(c1.x + 0.5, c1.y + 0.5);
        const b = this.camera.worldToScreen(c2.x + 0.5, c2.y + 0.5);
        const segmentMixed =
          hasColorInterferenceAt(state, analysis, c1) ||
          hasColorInterferenceAt(state, analysis, c2);
        const useMulti = segmentMixed || !feed;

        const width = util > 0.85 ? 3 : 2;
        let stroke: string;
        if (util > 0.9) {
          stroke = UI_PALETTE.danger;
        } else if (useMulti) {
          // Solid mixed color — gradients per segment are very expensive.
          stroke = COLOR_HEX.violet;
        } else {
          stroke = COLOR_HEX[feed!];
        }

        ctx.strokeStyle = stroke;
        ctx.lineWidth = width;
        ctx.lineCap = 'round';
        if (useGlow) {
          ctx.shadowColor = stroke;
          ctx.shadowBlur = util > 0.7 ? 8 : 4;
        }
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
        if (useGlow) ctx.shadowBlur = 0;
      }
    }
    for (const [k, v] of this.edgeLoadVisual) {
      if (v < 0.01) this.edgeLoadVisual.delete(k);
    }
  }

  /** Soft violet sparkles at cells where different-color cables cross (no relay). */
  private drawInterference(
    sim: Simulation,
    analysis: ReturnType<typeof buildNetworkAnalysis>,
  ): void {
    const cells = listInterferenceCells(sim.state, analysis);
    if (cells.length === 0) return;
    const ctx = this.ctx;
    const cell = this.camera.cellSize * this.camera.zoom;
    for (const c of cells) {
      const p = this.camera.worldToScreen(c.x + 0.5, c.y + 0.5);
      const pulse = 0.35 + Math.sin(this.time * 5 + c.x * 1.7 + c.y) * 0.2;
      const r = cell * (0.28 + pulse * 0.12);
      ctx.save();
      if (this.settings.glow) {
        ctx.shadowColor = '#c4a8ff';
        ctx.shadowBlur = 14;
      }
      ctx.strokeStyle = `rgba(196, 168, 255, ${0.35 + pulse * 0.45})`;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      ctx.stroke();
      // Tiny cross-hatch to suggest interference
      ctx.globalAlpha = 0.35 + pulse * 0.25;
      ctx.strokeStyle = '#ff7ac8';
      ctx.lineWidth = 1.2;
      const d = cell * 0.18;
      ctx.beginPath();
      ctx.moveTo(p.x - d, p.y - d);
      ctx.lineTo(p.x + d, p.y + d);
      ctx.moveTo(p.x + d, p.y - d);
      ctx.lineTo(p.x - d, p.y + d);
      ctx.stroke();
      ctx.restore();
    }
  }

  bumpEdgeLoad(c1: CellCoord, c2: CellCoord): void {
    const ek = edgeKeyFromCells(c1, c2);
    this.edgeLoadVisual.set(ek, (this.edgeLoadVisual.get(ek) ?? 0) + 0.15);
  }

  private drawPreview(path: CellCoord[], valid: boolean): void {
    const ctx = this.ctx;
    ctx.strokeStyle = valid ? 'rgba(87, 227, 161, 0.85)' : UI_PALETTE.danger;
    ctx.lineWidth = 3;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    for (let i = 0; i < path.length; i++) {
      const c = path[i]!;
      const p = this.camera.worldToScreen(c.x + 0.5, c.y + 0.5);
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    }
    ctx.stroke();
    ctx.setLineDash([]);
  }

  /** Continuous tip while drawing — grows inside the tip cell toward the exit. */
  private drawDraftStretch(s: NonNullable<RenderOverlay['draftStretch']>): void {
    const ctx = this.ctx;
    const a = this.camera.worldToScreen(s.fromX, s.fromY);
    const b = this.camera.worldToScreen(s.toX, s.toY);
    const stroke = s.valid
      ? `rgba(61, 216, 255, ${0.55 + s.progress * 0.4})`
      : UI_PALETTE.danger;
    ctx.save();
    ctx.strokeStyle = stroke;
    ctx.lineWidth = 2.5 + s.progress * 1.25;
    ctx.lineCap = 'round';
    if (this.settings.glow) {
      ctx.shadowColor = stroke;
      ctx.shadowBlur = 6 + s.progress * 6;
    }
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
    // Tip bead — reads as “building the cable”, not a finished segment.
    const r = (2.5 + s.progress * 2.5) * this.camera.zoom;
    ctx.fillStyle = stroke;
    ctx.beginPath();
    ctx.arc(b.x, b.y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private drawTutorialHint(state: Simulation['state']): void {
    const gen = state.nodes.find((n) => n.kind === 'generator');
    const bulb = state.nodes.find((n) => n.kind === 'bulb');
    if (!gen || !bulb) return;
    const ctx = this.ctx;
    const gp = nodePortCell(gen);
    const bp = nodePortCell(bulb);
    const g = this.camera.worldToScreen(gp.x + 0.5, gp.y + 0.5);
    const b = this.camera.worldToScreen(bp.x + 0.5, bp.y + 0.5);
    ctx.strokeStyle = `rgba(255, 209, 102, ${0.5 + Math.sin(this.time * 4) * 0.2})`;
    ctx.lineWidth = 2;
    ctx.setLineDash([8, 6]);
    ctx.beginPath();
    ctx.moveTo(g.x, g.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  /** Bulb-only: highlight the single valid attachment cell. */
  private drawPortSocket(node: GameNode, color: string): void {
    if (node.kind !== 'bulb') return;
    const port = nodePortCell(node);
    const p = this.camera.worldToScreen(port.x + 0.5, port.y + 0.5);
    const r = this.camera.cellSize * this.camera.zoom * 0.16;
    const ctx = this.ctx;
    ctx.save();
    ctx.strokeStyle = color;
    ctx.fillStyle = 'rgba(11, 20, 37, 0.85)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(p.x, p.y, r * 0.35, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private drawNodes(sim: Simulation, dt: number): void {
    for (const node of sim.state.nodes) {
      let anim = this.overlay.spawnAnim.get(node.id);
      if (anim !== undefined) {
        anim += dt * 2;
        if (anim >= 1) this.overlay.spawnAnim.delete(node.id);
        else this.overlay.spawnAnim.set(node.id, anim);
      }
      const scale = anim !== undefined ? 0.6 + anim * 0.4 : 1;
      if (node.kind === 'generator') this.drawGenerator(node, scale);
      else if (node.kind === 'bulb') this.drawBulb(sim, node, scale);
      else if (node.kind === 'teleporter') this.drawTeleporter(sim, node, scale);
      else if (node.kind === 'switch') this.drawSwitch(node, scale);
      else this.drawRelay(node, scale);
    }
    this.drawTeleporterLinks(sim);
  }

  /** Compact industrial generator filling one cell. */
  private drawGenerator(node: GameNode, scale: number): void {
    const ctx = this.ctx;
    const color = COLOR_HEX[node.color ?? 'cyan'];
    const tl = this.camera.worldToScreen(node.anchor.x, node.anchor.y);
    const cell = this.camera.cellSize * this.camera.zoom;
    const sz = cell * scale;
    const pad = cell * 0.08;
    const pulse = 0.7 + Math.sin(this.time * 6 + node.anchor.x) * 0.3;

    ctx.save();
    ctx.translate(tl.x + (cell - sz) / 2, tl.y + (cell - sz) / 2);

    // Chassis
    ctx.fillStyle = '#2a4a72';
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(pad, pad * 1.4, sz - pad * 2, sz - pad * 2.2, 3);
    ctx.fill();
    ctx.stroke();

    // Side vents
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 3; i++) {
      const vx = pad + 3;
      const vy = pad * 2.2 + i * (sz * 0.14);
      ctx.beginPath();
      ctx.moveTo(vx, vy);
      ctx.lineTo(vx + sz * 0.18, vy);
      ctx.stroke();
    }

    // Rotor / dynamo disc
    const cx = sz * 0.55;
    const cy = sz * 0.48;
    const rr = sz * 0.2;
    ctx.fillStyle = '#0d1524';
    ctx.beginPath();
    ctx.arc(cx, cy, rr, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, rr, 0, Math.PI * 2);
    ctx.stroke();
    // Spinning spokes
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(this.time * 3);
    ctx.strokeStyle = `${color}${Math.floor(pulse * 200).toString(16).padStart(2, '0')}`;
    for (let i = 0; i < 4; i++) {
      ctx.rotate(Math.PI / 2);
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(rr * 0.75, 0);
      ctx.stroke();
    }
    ctx.restore();

    // Status LED
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.5 + pulse * 0.5;
    ctx.beginPath();
    ctx.arc(sz - pad - 4, pad + 6, 2.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;

    // Base feet
    ctx.fillStyle = '#0a1220';
    ctx.fillRect(pad + 2, sz - pad * 1.1, sz * 0.22, pad * 0.6);
    ctx.fillRect(sz - pad - sz * 0.22 - 2, sz - pad * 1.1, sz * 0.22, pad * 0.6);

    if (this.settings.colorblindSymbols) {
      ctx.fillStyle = '#fff';
      ctx.font = `bold ${sz * 0.22}px sans-serif`;
      ctx.fillText('G', pad + 2, sz - pad);
    }
    ctx.restore();
  }

  /** Light-bulb in a 1×2 column (glass on top cell, screw/port on bottom). */
  private drawBulb(sim: Simulation, node: GameNode, scale: number): void {
    const ctx = this.ctx;
    const maxE = bulbMaxEnergy(sim.state);
    const energy = node.energy ?? maxE;
    const ratio = Math.max(0, Math.min(1, energy / maxE));
    const color = COLOR_HEX[node.color ?? 'cyan'];
    // Center of the 1×2 footprint
    const center = this.camera.worldToScreen(node.anchor.x + 0.5, node.anchor.y + 1);
    const cell = this.camera.cellSize * this.camera.zoom;
    const unit = cell * scale;
    const critical = ratio <= 0.25;
    const warning = ratio <= 0.5 && ratio > 0.25;
    const blink = critical && Math.sin(this.time * 10) > 0 ? 0.35 : 1;

    ctx.save();
    ctx.translate(center.x, center.y);

    // Low-energy alert aura: orange (50→25%), red (25→0%).
    if (warning || critical) {
      const auraColor = critical ? UI_PALETTE.danger : UI_PALETTE.orange;
      const pulse = 0.55 + Math.sin(this.time * (critical ? 7 : 4)) * 0.25;
      const auraR = unit * (critical ? 1.15 : 1.05) * (0.92 + pulse * 0.12);
      const g = ctx.createRadialGradient(0, -unit * 0.2, unit * 0.25, 0, -unit * 0.2, auraR);
      g.addColorStop(0, `${auraColor}00`);
      g.addColorStop(0.35, `${auraColor}${critical ? '66' : '44'}`);
      g.addColorStop(0.75, `${auraColor}${critical ? '55' : '33'}`);
      g.addColorStop(1, `${auraColor}00`);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.ellipse(0, -unit * 0.15, auraR * 0.85, auraR * 1.05, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = `${auraColor}${Math.round((0.35 + pulse * 0.45) * 255)
        .toString(16)
        .padStart(2, '0')}`;
      ctx.lineWidth = critical ? 2.5 : 2;
      ctx.beginPath();
      ctx.ellipse(0, -unit * 0.15, auraR * 0.72, auraR * 0.9, 0, 0, Math.PI * 2);
      ctx.stroke();
    }

    if (this.settings.glow && ratio > 0.12) {
      ctx.shadowColor = color;
      ctx.shadowBlur = 18 * ratio;
    }

    const glassW = unit * 0.78;
    const glassH = unit * 1.05;

    // Glass (upper portion)
    ctx.beginPath();
    ctx.ellipse(0, -unit * 0.55, glassW * 0.48, glassH * 0.42, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
    ctx.fill();

    ctx.save();
    ctx.beginPath();
    ctx.ellipse(0, -unit * 0.55, glassW * 0.48, glassH * 0.42, 0, 0, Math.PI * 2);
    ctx.clip();
    const fillTop = -unit * 0.15 - ratio * glassH * 0.75;
    const grad = ctx.createLinearGradient(0, unit * 0.05, 0, -unit * 1.1);
    grad.addColorStop(0, color);
    grad.addColorStop(1, `${color}aa`);
    ctx.fillStyle = grad;
    ctx.globalAlpha = 0.65 + ratio * 0.35 * blink;
    ctx.fillRect(-glassW * 0.5, fillTop, glassW, unit * 0.2 - fillTop);
    ctx.globalAlpha = 1;
    ctx.restore();

    ctx.beginPath();
    ctx.ellipse(0, -unit * 0.55, glassW * 0.48, glassH * 0.42, 0, 0, Math.PI * 2);
    ctx.strokeStyle = critical
      ? UI_PALETTE.danger
      : warning
        ? UI_PALETTE.orange
        : 'rgba(255, 255, 255, 0.55)';
    ctx.lineWidth = critical || warning ? 2.5 : 2;
    ctx.stroke();
    ctx.shadowBlur = 0;

    // Filament
    ctx.strokeStyle = color;
    ctx.globalAlpha = 0.35 + ratio * 0.65 * blink;
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.moveTo(-unit * 0.12, -unit * 0.35);
    ctx.quadraticCurveTo(0, -unit * 0.75, unit * 0.12, -unit * 0.35);
    ctx.stroke();
    ctx.globalAlpha = 1;

    // Tiny % between glass and screw base
    ctx.fillStyle = critical
      ? UI_PALETTE.danger
      : warning
        ? UI_PALETTE.orange
        : '#f2f7ff';
    ctx.font = `600 ${Math.max(9, unit * 0.2)}px DM Sans, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${Math.round(ratio * 100)}%`, 0, -unit * 0.02);

    // Consumption tier pips (1–4) — readable at a glance, neutral metal.
    const tierIdx = Math.max(
      0,
      CONSUMPTION_TIERS.findIndex((t) => t.id === (node.consumptionTier ?? 'standard')),
    );
    const pipCount = tierIdx + 1;
    const pipY = unit * 0.1;
    const pipGap = unit * (pipCount >= 4 ? 0.13 : 0.15);
    const pipR = unit * (pipCount >= 3 ? 0.055 : 0.05);
    const pipStartX = -((pipCount - 1) * pipGap) / 2;
    for (let i = 0; i < pipCount; i++) {
      const px = pipStartX + i * pipGap;
      ctx.beginPath();
      ctx.arc(px, pipY, pipR, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(242, 247, 255, 0.92)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(30, 40, 60, 0.55)';
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // Neck + screw base (bottom cell = connection)
    ctx.fillStyle = '#3d4f6a';
    ctx.fillRect(-unit * 0.18, unit * 0.16, unit * 0.36, unit * 0.08);
    ctx.fillStyle = '#b8c2d4';
    ctx.beginPath();
    ctx.roundRect(-unit * 0.22, unit * 0.22, unit * 0.44, unit * 0.42, 3);
    ctx.fill();
    ctx.strokeStyle = 'rgba(30, 40, 60, 0.45)';
    for (let i = 0; i < 3; i++) {
      const y = unit * 0.3 + i * unit * 0.1;
      ctx.beginPath();
      ctx.moveTo(-unit * 0.2, y);
      ctx.lineTo(unit * 0.2, y);
      ctx.stroke();
    }
    // Contact tip (port)
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(0, unit * 0.72, unit * 0.1, 0, Math.PI * 2);
    ctx.fill();

    if (this.settings.colorblindSymbols) {
      ctx.fillStyle = '#fff';
      ctx.font = `bold ${unit * 0.3}px sans-serif`;
      ctx.fillText('B', -unit * 0.1, -unit * 0.5);
    }
    ctx.restore();
    this.drawPortSocket(node, color);
  }

  private drawNodeHoverTooltip(sim: Simulation): void {
    const hover = this.overlay.hoverCell;
    if (!hover || this.overlay.previewPath) return;

    const node = sim.state.nodes.find(
      (n) =>
        (n.kind === 'bulb' || n.kind === 'generator') && nodeOccupiesCell(n, hover),
    );
    if (!node) return;

    const color = COLOR_HEX[node.color ?? 'cyan'];
    let lines: string[];
    let accentDanger = false;
    let center: { x: number; y: number };
    let lift = 1.35;

    if (node.kind === 'bulb') {
      const maxE = bulbMaxEnergy(sim.state);
      const energy = node.energy ?? maxE;
      const pct = Math.round((energy / maxE) * 100);
      const inflow = bulbInflowPerSecond(sim.state, node.id);
      const drain = consumptionRateForTier(node.consumptionTier);
      const net = inflow - drain;
      accentDanger = net < 0;
      lines = [
        `${pct}%  ·  ${energy.toFixed(0)} / ${maxE.toFixed(0)}`,
        `Reçu ${inflow.toFixed(1)}/s`,
        `Conso ${drain.toFixed(1)}/s  ·  Net ${net >= 0 ? '+' : ''}${net.toFixed(1)}/s`,
      ];
      center = this.camera.worldToScreen(node.anchor.x + 0.5, node.anchor.y + 1);
    } else {
      const prod = effectiveGeneratorProductionPerSecond(sim.state, node);
      const net = networkEnergyStatsForGenerator(sim.state, node);
      const bulbLabel =
        net.bulbCount === 0
          ? 'Aucune ampoule reliée'
          : net.bulbCount === 1
            ? '1 ampoule reliée'
            : `${net.bulbCount} ampoules reliées`;
      const balance = net.networkProduction - net.networkConsumption;
      accentDanger = net.bulbCount > 0 && balance < 0;
      lines = [
        `Production ${prod.toFixed(1)}/s`,
        bulbLabel,
        `Réseau prod ${net.networkProduction.toFixed(1)}/s`,
        `Réseau conso ${net.networkConsumption.toFixed(1)}/s`,
        `Réseau net ${balance >= 0 ? '+' : ''}${balance.toFixed(1)}/s`,
      ];
      center = this.camera.worldToScreen(node.anchor.x + 0.5, node.anchor.y + 0.5);
      lift = 1.1;
    }

    const ctx = this.ctx;
    ctx.save();
    ctx.font = '12px DM Sans, sans-serif';
    const pad = 10;
    const lineH = 16;
    let tw = 0;
    for (const line of lines) tw = Math.max(tw, ctx.measureText(line).width);
    const boxW = tw + pad * 2;
    const boxH = lines.length * lineH + pad * 2;
    let x = center.x - boxW / 2;
    let y = center.y - this.camera.cellSize * this.camera.zoom * lift - boxH;
    x = Math.max(8, Math.min(x, this.canvas.clientWidth - boxW - 8));
    y = Math.max(8, y);

    ctx.fillStyle = 'rgba(11, 20, 37, 0.92)';
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.roundRect(x, y, boxW, boxH, 8);
    ctx.fill();
    ctx.stroke();

    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    lines.forEach((line, i) => {
      const isNetLine = line.startsWith('Réseau net') || (node.kind === 'bulb' && i === 2);
      ctx.fillStyle =
        i === 0 ? color : accentDanger && isNetLine ? UI_PALETTE.danger : '#c5d4ea';
      ctx.fillText(line, x + pad, y + pad + i * lineH);
    });
    ctx.restore();
  }

  /** Dashed wormhole link between paired portals. */
  private drawTeleporterLinks(sim: Simulation): void {
    const ctx = this.ctx;
    const drawn = new Set<string>();
    for (const node of sim.state.nodes) {
      if (node.kind !== 'teleporter' || !node.linkedNodeId) continue;
      const key = [node.id, node.linkedNodeId].sort().join('|');
      if (drawn.has(key)) continue;
      drawn.add(key);
      const other = sim.state.nodes.find((n) => n.id === node.linkedNodeId);
      if (!other) continue;
      const a = this.camera.worldToScreen(node.anchor.x + 0.5, node.anchor.y + 0.5);
      const b = this.camera.worldToScreen(other.anchor.x + 0.5, other.anchor.y + 0.5);
      ctx.save();
      ctx.strokeStyle = `rgba(180, 140, 255, ${0.35 + Math.sin(this.time * 3) * 0.1})`;
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 5]);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }
  }

  /** 1×1 portal ring — pair A/B linked across the map. */
  private drawTeleporter(sim: Simulation, node: GameNode, scale: number): void {
    void sim;
    const ctx = this.ctx;
    const p = this.camera.worldToScreen(node.anchor.x + 0.5, node.anchor.y + 0.5);
    const cell = this.camera.cellSize * this.camera.zoom;
    const r = cell * 0.34 * scale;
    const linked = !!node.linkedNodeId;
    const hue = linked ? '#b48cff' : '#f0c36a';
    ctx.save();
    ctx.translate(p.x, p.y);
    if (this.settings.glow) {
      ctx.shadowColor = hue;
      ctx.shadowBlur = 12;
    }
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.fillStyle = '#1a1430';
    ctx.fill();
    ctx.strokeStyle = hue;
    ctx.lineWidth = 2.5;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.55, 0, Math.PI * 2);
    ctx.strokeStyle = linked ? '#e8d6ff' : '#ffe6a8';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.fillStyle = '#f4eefe';
    ctx.font = `bold ${Math.max(10, cell * 0.28)}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(node.teleporterSlot ?? '?', 0, 0);
    ctx.restore();
  }

  /** Single-cell relay diamond that cancels local interference. */
  private drawRelay(node: GameNode, scale: number): void {
    const ctx = this.ctx;
    const p = this.camera.worldToScreen(node.anchor.x + 0.5, node.anchor.y + 0.5);
    const cell = this.camera.cellSize * this.camera.zoom;
    const s = cell * 0.32 * scale;
    ctx.save();
    ctx.translate(p.x, p.y);
    if (this.settings.glow) {
      ctx.shadowColor = '#5ef0b0';
      ctx.shadowBlur = 10;
    }
    ctx.rotate(Math.PI / 4);
    ctx.fillStyle = '#2a6b7a';
    ctx.fillRect(-s, -s, s * 2, s * 2);
    ctx.strokeStyle = '#5ef0b0';
    ctx.lineWidth = 2;
    ctx.strokeRect(-s, -s, s * 2, s * 2);
    ctx.shadowBlur = 0;
    ctx.fillStyle = '#e8f0ff';
    ctx.fillRect(-s * 0.28, -s * 0.28, s * 0.56, s * 0.56);
    ctx.restore();
  }

  /** Switch: keeps crossing networks from mixing (amber cross). */
  private drawSwitch(node: GameNode, scale: number): void {
    const ctx = this.ctx;
    const p = this.camera.worldToScreen(node.anchor.x + 0.5, node.anchor.y + 0.5);
    const cell = this.camera.cellSize * this.camera.zoom;
    const s = cell * 0.34 * scale;
    ctx.save();
    ctx.translate(p.x, p.y);
    if (this.settings.glow) {
      ctx.shadowColor = '#ffb347';
      ctx.shadowBlur = 12;
    }
    ctx.fillStyle = '#5a3d1a';
    ctx.beginPath();
    ctx.roundRect(-s, -s, s * 2, s * 2, 4);
    ctx.fill();
    ctx.strokeStyle = '#ffb347';
    ctx.lineWidth = 2.2;
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.strokeStyle = '#ffe0a8';
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(-s * 0.55, -s * 0.55);
    ctx.lineTo(s * 0.55, s * 0.55);
    ctx.moveTo(s * 0.55, -s * 0.55);
    ctx.lineTo(-s * 0.55, s * 0.55);
    ctx.stroke();
    ctx.restore();
  }

  private drawPulses(
    sim: Simulation,
    cableById: Map<string, { cells: CellCoord[] }>,
    heavy: boolean,
    veryHeavy: boolean,
    smoothPos: Map<string, { x: number; y: number }>,
  ): void {
    const ctx = this.ctx;
    const pulses = sim.state.pulses;
    const n = pulses.length;
    // Under extreme load, draw a subset — gameplay is unchanged.
    const stride = veryHeavy ? Math.ceil(n / 180) : heavy ? Math.ceil(n / 220) : 1;
    const r = (heavy ? 3.2 : 4) * this.camera.zoom;
    const glow = this.settings.glow && !heavy;
    const drawTrails = glow && !heavy;

    // Group by color to cut fillStyle / shadow state changes.
    const byColor = new Map<ColorId, typeof pulses>();
    for (let i = 0; i < n; i += stride) {
      const pulse = pulses[i]!;
      let list = byColor.get(pulse.color);
      if (!list) {
        list = [];
        byColor.set(pulse.color, list);
      }
      list.push(pulse);
    }

    for (const [colorId, list] of byColor) {
      const color = COLOR_HEX[colorId];
      if (drawTrails) {
        ctx.strokeStyle = `${color}44`;
        ctx.lineWidth = 3;
        for (const pulse of list) {
          const trail = this.pulseTrails.get(pulse.id);
          if (!trail || trail.length < 2) continue;
          ctx.beginPath();
          for (let i = 0; i < trail.length; i++) {
            const pt = trail[i]!;
            if (i === 0) ctx.moveTo(pt.x, pt.y);
            else ctx.lineTo(pt.x, pt.y);
          }
          ctx.stroke();
        }
      }

      ctx.fillStyle = color;
      if (glow) {
        ctx.shadowColor = color;
        ctx.shadowBlur = 8;
      }
      ctx.beginPath();
      for (const pulse of list) {
        const pos = smoothPos.get(pulse.id);
        if (!pos) continue;
        const screen = this.camera.worldToScreen(pos.x, pos.y);
        ctx.moveTo(screen.x + r, screen.y);
        ctx.arc(screen.x, screen.y, r, 0, Math.PI * 2);
      }
      ctx.fill();
      if (glow) ctx.shadowBlur = 0;
    }
  }

  deliveryFlash(nodeId: string, sim: Simulation): void {
    const node = sim.state.nodes.find((n) => n.id === nodeId);
    if (!node || node.kind !== 'bulb') return;
    const c = cellCenterCoord({ x: node.anchor.x, y: node.anchor.y + 1 });
    const p = this.camera.worldToScreen(c.cx, c.cy);
    const color = COLOR_HEX[node.color ?? 'cyan'];
    if (this.settings.particles) this.particles.burst(p.x, p.y, color);
  }

  connectFlash(c1: CellCoord, c2: CellCoord): void {
    const a = this.camera.worldToScreen(c1.x + 0.5, c1.y + 0.5);
    const b = this.camera.worldToScreen(c2.x + 0.5, c2.y + 0.5);
    if (this.settings.particles) this.particles.sparkLine(a.x, a.y, b.x, b.y, '#57E3A1');
  }
}
